import React from 'react';
import { BellRing, ChevronRight, Clock3, Siren } from 'lucide-react';

const WARNING_TYPES = new Set(['EARLY_WARNING', 'EARLY_WARNING_DISPATCH']);

const formatNotificationTime = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
};

const formatNotificationType = (notification) => {
  const value = notification?.category || notification?.type;
  if (!value) return 'Notification';
  if (WARNING_TYPES.has(notification?.type)) return 'Early warning';
  return String(value)
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const NotificationItem = ({ notification, onClick, className = '' }) => {
  const unread = notification?.read === false || notification?.read === 0;
  const isWarning = WARNING_TYPES.has(notification?.type);
  const Icon = isWarning ? Siren : BellRing;
  const time = formatNotificationTime(notification?.created_at);
  const type = formatNotificationType(notification);
  const title = notification?.title || 'Notification';

  return (
    <button
      type="button"
      className={`group relative grid w-full grid-cols-[42px_minmax(0,1fr)_auto] items-start gap-3.5 px-4 py-4 text-start transition sm:px-5 ${
        unread
          ? 'bg-gradient-to-r from-indigo-50/90 via-white to-white hover:from-indigo-100/80'
          : 'bg-white hover:bg-slate-50/90'
      } focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 ${className}`}
      onClick={() => onClick(notification)}
      aria-label={`${title}. ${unread ? 'Unread' : 'Read'}${time ? `. ${time}` : ''}`}
    >
      {unread && (
        <span
          className="absolute inset-y-3 left-0 w-[3px] rounded-r-full bg-indigo-500"
          aria-hidden="true"
        />
      )}

      <span
        className={`relative flex h-[42px] w-[42px] items-center justify-center rounded-xl border ${
          isWarning
            ? 'border-rose-200 bg-rose-50 text-rose-600'
            : unread
              ? 'border-indigo-200 bg-indigo-100 text-indigo-700'
              : 'border-slate-200 bg-slate-50 text-slate-500'
        }`}
        aria-hidden="true"
      >
        <Icon className="h-[18px] w-[18px]" />
        {unread && (
          <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-indigo-500" />
        )}
      </span>

      <span className="min-w-0">
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className={`min-w-0 truncate text-[14px] text-slate-900 ${unread ? 'font-extrabold' : 'font-bold'}`}>
            {title}
          </span>
          {unread && (
            <span className="inline-flex h-5 flex-none items-center rounded-full bg-indigo-100 px-2 text-[10px] font-black uppercase tracking-[0.08em] text-indigo-700">
              Unread
            </span>
          )}
        </span>

        {notification?.message && (
          <span className="mt-1 block max-w-4xl text-[13px] leading-5 text-slate-600 line-clamp-2">
            {notification.message}
          </span>
        )}

        <span className="mt-2 flex flex-wrap items-center gap-2 text-[11px] font-semibold text-slate-400">
          <span className={isWarning ? 'text-rose-500' : 'text-slate-500'}>{type}</span>
          {time && (
            <>
              <span className="h-1 w-1 rounded-full bg-slate-300" aria-hidden="true" />
              <span className="inline-flex items-center gap-1" dir="ltr">
                <Clock3 className="h-3 w-3" aria-hidden="true" />
                {time}
              </span>
            </>
          )}
        </span>
      </span>

      <ChevronRight
        className="mt-3 h-4 w-4 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-slate-500"
        aria-hidden="true"
      />
    </button>
  );
};

export default NotificationItem;
